import type { AIAgent } from '@app/services/api.types';
import {
    Descriptions,
    Divider,
    Drawer,
    Empty,
    Flex,
    type DescriptionsProps,
    type DrawerProps,
} from 'antd';
import useAgent from '@app/hooks/agents/useAgent';
import { StyledButton } from '@app/components/button/Button';
import { HiLink } from 'react-icons/hi2';
import { useNavigate } from 'react-router';
import { url } from '@app/utils/constants';

export type AgentDrawerProps = DrawerProps & {
    data: AIAgent | null;
};

export const AgentDrawer = ({ data, ...props }: AgentDrawerProps) => {
    const navigate = useNavigate();
    const { data: agentData } = useAgent(
        { id: data?.id || '' },
        {
            enabled: Boolean(data?.id),
        }
    );

    const detailsItems: DescriptionsProps['items'] = [
        {
            key: '1',
            label: 'Name',
            children: data?.name || '-',
        },
        {
            key: '2',
            label: 'Type',
            children: data?.type_name || '-',
        },
        {
            key: '3',
            label: 'Prompt',
            span: 2,
            children:
                'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse non fringilla dui. Vestibulum finibus tortor urna, ac placerat arcu suscipit nec. Praesent fermentum orci ligula, non rhoncus quam vehicula vel. Curabitur lectus purus, feugiat at sodales a, sollicitudin id eros. Nullam fringilla maximus magna, nec pulvinar turpis pretium a. Suspendisse congue venenatis nibh, a iaculis lectus interdum eu. Praesent eu tincidunt dui.',
        },
    ];

    const numberItems: DescriptionsProps['items'][] =
        agentData?.numbers?.map((item, index) => [
            {
                key: `brand_${index}`,
                label: 'Brand',
                children: item.brand || '-',
            },
            {
                key: `location_${index}`,
                label: 'Location',
                children: item.location || '-',
            },
            {
                key: `number_${index}`,
                label: 'Agent Number',
                children: item.number || '-',
            },
            {
                key: `failover_${index}`,
                label: 'Failover Number',
                children: item.failover_number || '-',
            },
        ]) || [];

    const renderAssignedNumbers = () => {
        if (!numberItems.length) {
            return (
                <Descriptions
                    items={[
                        {
                            key: 1,
                            label: '',
                            children: (
                                <Empty
                                    description="No numbers assigned"
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    style={{ margin: 'auto' }}>
                                    <StyledButton
                                        size="small"
                                        icon={<HiLink />}
                                        onClick={() => {
                                            navigate(
                                                url.adminPortal.internalAdmin.assignAgent.numbers
                                            );
                                        }}>
                                        Assign Number
                                    </StyledButton>
                                </Empty>
                            ),
                        },
                    ]}
                />
            );
        }

        return numberItems?.map((items, index) => (
            <Descriptions
                key={index}
                colon={false}
                layout="vertical"
                items={items}
                column={2}
                className="fixed-cols numbers-assigned-item"
            />
        ));
    };

    const renderDetailsTab = () => {
        return (
            <>
                <Descriptions
                    title="Details"
                    colon={false}
                    layout="vertical"
                    items={detailsItems}
                    column={2}
                />
                <Divider />
                <Descriptions title="Numbers Assigned" />
                {renderAssignedNumbers()}
            </>
        );
    };

    return (
        <Drawer
            title={
                <>
                    <Flex align="center" justify="space-between" gap={12}>
                        <span>AI Agent Details</span>
                    </Flex>
                    {data?.id ? (
                        <div className="ant-drawer-header-meta">
                            <span>ID: {data?.id}</span>
                        </div>
                    ) : null}
                </>
            }
            size={700}
            closable={{ 'aria-label': 'Close Button' }}
            {...props}>
            {renderDetailsTab()}
        </Drawer>
    );
};

export default AgentDrawer;
